import React, { useEffect, useState, useRef } from "react";
import { useLocation } from 'react-router-dom';
import {
  FaShieldAlt,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaFileAlt,
  FaFolderOpen,
  FaLayerGroup,
  FaScroll,
  FaGripVertical,
  FaChevronDown,
  FaAngleDoubleDown,
  FaAngleDoubleUp,
} from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import Modal from '../components/Modal/Modal';
import PageHero from '../components/PageHero/PageHero';
import "./Policy.scss";
import { storage } from "../utils/storage";
import { STORAGE_KEYS, API_BASE_URL } from "../utils/constants";
import session from "../utils/session";
import { formatDateISO } from '../utils/date';
import useConfirm from '../hooks/useConfirm';
import { useLanguage } from '../context/LanguageContext';
import showToast from '../utils/toast';

// Unique stable id used for drag & drop (matches normalizePolicies keys)
const idOf = (p) =>
  p && p._id != null ? `id:${p._id}` : p && p.id != null ? `local:${p.id}` : null;

const SortablePolicyItem = ({ id, rawId, index, content, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`policy__item${isDragging ? ' policy__item--dragging' : ''}`}
    >
      <button
        className="policy__drag-handle"
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <FaGripVertical />
      </button>
      <span className="policy__item-no">{index + 1}</span>
      <div className="policy__item-content">{content}</div>
      <div className="policy__item-actions">
        <button
          className="policy__action-btn policy__action-btn--edit"
          onClick={() => onEdit(rawId)}
          title="Edit"
        >
          <FaEdit />
        </button>
        <button
          className="policy__action-btn policy__action-btn--delete"
          onClick={() => onDelete(rawId)}
          title="Delete"
        >
          <FaTrash />
        </button>
      </div>
    </div>
  );
};

const Policy = () => {
  const { t } = useLanguage();
  const [text, setText] = useState("");
  const [policies, setPolicies] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [filterGroup, setFilterGroup] = useState("all");
  // Collapsed groups map: { [groupName]: true } — absent/false means expanded.
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Normalize and dedupe policies by server _id when present, otherwise by local id.
  const normalizePolicies = (arr) => {
    if (!Array.isArray(arr)) return [];
    const map = new Map();
    for (const p of arr) {
      if (!p || typeof p !== 'object') continue;
      const key = p._id ? `id:${p._id}` : p.id ? `local:${p.id}` : `idx:${Math.random()}`;
      map.set(key, p);
    }
    return Array.from(map.values());
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = session.getToken();
        if (token) {
          const res = await fetch(`${API_BASE_URL}/policy`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) {
              setPolicies(normalizePolicies(data));
              return;
            }
          }
        }
      } catch (e) {
        // ignore and fall back to local
      }
      const saved = storage.getJSON(STORAGE_KEYS.POLICY, []);
      if (Array.isArray(saved)) setPolicies(normalizePolicies(saved));
    };
    load();
  }, []);

  const confirm = useConfirm();

  const location = useLocation();
  const pendingPolicyIdRef = useRef(null);
  const [highlightedPolicyId, setHighlightedPolicyId] = useState(null);

  useEffect(() => {
    if (location?.state?.openAdd) {
      toggleForm();
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }
    // Deep-link for push clicks when app was closed (SW opens /policy?id=...)
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const p = policies.find(x => String(x._id) === String(qId) || String(x.id) === String(qId));
        if (p) {
          setHighlightedPolicyId(String(qId));
          handleEdit(qId);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          setTimeout(() => {
            const el = document.querySelector(`[data-policy-id="${qId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        } else {
          pendingPolicyIdRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingPolicyIdRef.current && policies.length) {
      const p = policies.find(x => String(x._id) === String(pendingPolicyIdRef.current) || String(x.id) === String(pendingPolicyIdRef.current));
      if (p) {
        setHighlightedPolicyId(pendingPolicyIdRef.current);
        handleEdit(pendingPolicyIdRef.current);
        pendingPolicyIdRef.current = null;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } catch (e) {}
      }
    }
  }, [policies]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const p = policies.find(x => String(x._id) === String(qId) || String(x.id) === String(qId));
        if (p) {
          setHighlightedPolicyId(String(qId));
          handleEdit(String(qId));
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/policy') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const p = policies.find(x => String(x._id) === String(state.openViewId) || String(x.id) === String(state.openViewId));
          if (p) {
            setHighlightedPolicyId(String(state.openViewId));
            handleEdit(String(state.openViewId));
          } else pendingPolicyIdRef.current = String(state.openViewId);
        } else if (state.openAdd) toggleForm();
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [policies]);

  const persist = (next) => {
    setPolicies(normalizePolicies(next));
    try { storage.setJSONAndSync(STORAGE_KEYS.POLICY, next); } catch (e) {}
    // Try server sync for the full list when possible. This is best-effort.
    (async () => {
      try {
        const token = session.getToken();
        if (!token) {
    try { storage.setJSONAndSync(STORAGE_KEYS.POLICY, next); } catch (e) {}
          return;
        }
        // Only sync items that already have a server-side `_id`.
        // New/local items (which only have `id`) are created via the explicit
        // add flow (handleAdd) to avoid PUTting to numeric/local IDs which
        // the server may not recognise and which caused 500s.
        for (const item of next) {
          if (!item || typeof item !== 'object') continue;
          // Only attempt to update server-side items
          const serverId = item._id || null;
          if (!serverId) continue;
          try {
            await fetch(`${API_BASE_URL}/policy/${encodeURIComponent(serverId)}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(item),
            });
          } catch (e) {
            // ignore per-item errors
          }
        }
      } catch (e) {
        try { storage.setJSONAndSync(STORAGE_KEYS.POLICY, next); } catch (err) {}
      }
    })();
  };

  // Reorder a policy within its own group (drag & drop).
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const findPolicy = (pid) => policies.find((p) => idOf(p) === pid);
    const activePolicy = findPolicy(active.id);
    const overPolicy = findPolicy(over.id);
    if (!activePolicy || !overPolicy) return;
    const activeGroup = activePolicy.group || "Uncategorized";
    if ((overPolicy.group || "Uncategorized") !== activeGroup) return;

    const groupItems = policies.filter((p) => (p.group || "Uncategorized") === activeGroup);
    const fromIdx = groupItems.findIndex((p) => idOf(p) === active.id);
    const toIdx = groupItems.findIndex((p) => idOf(p) === over.id);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = arrayMove(groupItems, fromIdx, toIdx);
    const reorderedKeys = new Set(reordered.map(idOf));
    const next = [];
    let g = 0;
    for (const p of policies) {
      if (reorderedKeys.has(idOf(p))) next.push(reordered[g++]);
      else next.push(p);
    }
    // Persist a global sort order so the server returns the same layout.
    persist(next.map((p, i) => ({ ...p, sortOrder: i * 10 })));
  };

  const handleAdd = () => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    // If editing an existing entry
    if (editingIndex > -1) {
      const next = [...policies];
      next[editingIndex] = {
        ...next[editingIndex],
        content: trimmed,
        group: groupName.trim() || "Uncategorized",
        updatedAt: formatDateISO(new Date()),
      };
      // Optimistic update + server update
      persist(next);
      (async () => {
        try {
          const token = session.getToken();
          if (!token) return;
          const id = next[editingIndex]._id || next[editingIndex].id;
          const res = await fetch(`${API_BASE_URL}/policy/${encodeURIComponent(id)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(next[editingIndex]),
          });
          if (res.ok) {
            showToast('Policy updated successfully', { type: 'success' });
          }
        } catch (e) {}
      })();
      setEditingIndex(-1);
      setText("");
      setGroupName("");
      setShowForm(false);
      return;
    }

    // Add new entry
    const newItem = {
      id: Date.now(),
      content: trimmed,
      group: groupName.trim() || "Uncategorized",
      createdAt: formatDateISO(new Date()),
      sortOrder: policies.length
        ? Math.min(...policies.map((p) => Number(p.sortOrder) || 0)) - 1
        : 0,
    };
    const next = [newItem, ...policies];
    // Local optimistic update only — persist() would PUT every existing item
    setPolicies(normalizePolicies(next));
    try { storage.setJSONAndSync(STORAGE_KEYS.POLICY, next); } catch (e) {}
    // Server create
    (async () => {
      try {
        const token = session.getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/policy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(newItem),
        });
        if (res.ok) {
          const created = await res.json();
          setPolicies((prev) => normalizePolicies(prev.map((p) => (p.id === newItem.id ? created : p))));
          try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}
        }
      } catch (e) {}
    })();
    setText("");
    setGroupName("");
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    const policy = policies.find((p) => (p._id ? String(p._id) === String(id) : p.id === id));
    const preview = policy && policy.content ? `${String(policy.content).slice(0, 120)}${(policy.content.length||0) > 120 ? '...' : ''}` : '';
    const ok = await confirm(`Delete this term${preview ? `: "${preview}"` : ''}?`);
    if (!ok) return;

    const next = policies.filter((p) => {
      if (p._id) return String(p._id) !== String(id);
      return p.id !== id;
    });

    // Optimistic UI update
    setPolicies(normalizePolicies(next));

    try {
      const token = session.getToken();
      // If this item was never created on the server (no `_id`), don't
      // attempt a server DELETE — just persist locally. This prevents
      // sending numeric/local ids (Date.now()) to the API which return 500.
      if (!policy || !policy._id) {
        try { persist(next); } catch (e) {}
        return;
      }

      if (!token) {
        // No auth token — persist locally (best-effort)
        try { persist(next); } catch (e) {}
        return;
      }

      // Attempt server delete first to avoid re-creating the item during upserts
      const res = await fetch(`${API_BASE_URL}/policy/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        // Server failed to delete — reload from server to restore canonical state
        try {
          const reload = await fetch(`${API_BASE_URL}/policy`, { headers: { Authorization: `Bearer ${token}` } });
          if (reload.ok) {
            const data = await reload.json();
            if (Array.isArray(data)) setPolicies(normalizePolicies(data));
          }
        } catch (e) {
          // ignore reload errors
        }
        return;
      }

      // Delete succeeded on server — persist remaining items to ensure server has them
      persist(next);
    } catch (e) {
      // Network or unexpected error — try to recover by reloading server state if possible
      try {
        const token = session.getToken();
        if (token) {
          const reload = await fetch(`${API_BASE_URL}/policy`, { headers: { Authorization: `Bearer ${token}` } });
          if (reload.ok) {
            const data = await reload.json();
            if (Array.isArray(data)) setPolicies(normalizePolicies(data));
          }
        } else {
          persist(next);
        }
      } catch (err) {
        // swallow
      }
    }
  };

  const handleEdit = (id) => {
    const index = policies.findIndex((p) => (p._id ? String(p._id) === String(id) : p.id === id));
    if (index === -1) return;
    setEditingIndex(index);
    setText(policies[index].content || "");
    const grp = policies[index].group || "Uncategorized";
    setGroupName(grp);
    setSelectedGroup(grp);
    setShowNewGroupInput(false);
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingIndex(-1);
    setText("");
    setGroupName("");
    setSelectedGroup("");
    setShowNewGroupInput(false);
    setShowForm(false);
  };

  const toggleForm = () => {
    setEditingIndex(-1);
    setText("");
    // Default to first existing group when opening the form, or Uncategorized
    const groups = getGroups();
    const defaultGroup = groups && groups.length ? groups[0] : "Uncategorized";
    setSelectedGroup(defaultGroup);
    setGroupName(defaultGroup);
    setShowNewGroupInput(false);
    setShowForm(true);
  };

  const onGroupSelectChange = (e) => {
    const val = e.target.value;
    if (val === "__new") {
      setSelectedGroup("__new");
      setShowNewGroupInput(true);
      setGroupName("");
    } else {
      setSelectedGroup(val);
      setShowNewGroupInput(false);
      setGroupName(val);
    }
  };

  // Get unique groups from policies
  const getGroups = () => {
    const groups = new Set(policies.map(p => p.group || "Uncategorized"));
    return Array.from(groups).sort();
  };

  // Get filtered policies
  const getFilteredPolicies = () => {
    return policies.filter((p) => {
      const matchesSearch = (p.content || "").toLowerCase().includes(search.toLowerCase()) ||
                           (p.group || "").toLowerCase().includes(search.toLowerCase());
      const matchesGroup = filterGroup === "all" || (p.group || "Uncategorized") === filterGroup;
      return matchesSearch && matchesGroup;
    });
  };

  // Group policies by group name
  const getGroupedPolicies = () => {
    const filtered = getFilteredPolicies();
    const grouped = {};
    filtered.forEach(policy => {
      const group = policy.group || "Uncategorized";
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(policy);
    });
    return grouped;
  };

  const groupedPolicies = getGroupedPolicies();
  const groupNames = Object.keys(groupedPolicies);
  const collapsedCount = groupNames.filter((g) => collapsedGroups[g]).length;
  const allCollapsed = groupNames.length > 0 && collapsedCount === groupNames.length;

  const isGroupCollapsed = (group) => !!collapsedGroups[group];

  const toggleGroup = (group) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const expandAllGroups = () => setCollapsedGroups({});
  const collapseAllGroups = () => {
    const next = {};
    groupNames.forEach((g) => { next[g] = true; });
    setCollapsedGroups(next);
  };
  const toggleAllGroups = () => {
    if (allCollapsed) expandAllGroups();
    else collapseAllGroups();
  };

  // When searching/filtering, auto-expand so matches are never hidden inside a collapsed group.
  useEffect(() => {
    if (search || filterGroup !== "all") setCollapsedGroups({});
  }, [search, filterGroup]);

  // Drop collapse state for groups that no longer exist.
  useEffect(() => {
    setCollapsedGroups((prev) => {
      const valid = new Set(getGroups());
      const next = {};
      Object.keys(prev).forEach((g) => {
        if (valid.has(g) && !prev[g] === false) next[g] = prev[g];
        if (valid.has(g) && prev[g]) next[g] = true;
      });
      return Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((k) => next[k] === prev[k])
        ? prev
        : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies]);

  // Calculate stats
  const totalTerms = policies.length;
  const totalGroups = getGroups().length;
  const ungroupedCount = policies.filter(p => !p.group || p.group === 'Uncategorized').length;

  return (
    <div className="policy">
      {/* Hero */}
      <PageHero
        icon={<FaShieldAlt />}
        title={t('policy.title')}
        subtitle={t('policy.subtitle')}
        actions={
          <button className="page-hero__btn" onClick={toggleForm}>
            <FaPlus />
            {t('policy.addTerm')}
          </button>
        }
      />

      {/* Stats */}
      <div className="policy__stats">
        <div className="policy__stat-card">
          <div className="policy__stat-icon policy__stat-icon--indigo">
            <FaFileAlt />
          </div>
          <div className="policy__stat-info">
            <h3>{totalTerms}</h3>
            <p>{t('policy.totalTerms')}</p>
          </div>
        </div>
        <div className="policy__stat-card">
          <div className="policy__stat-icon policy__stat-icon--emerald">
            <FaLayerGroup />
          </div>
          <div className="policy__stat-info">
            <h3>{totalGroups}</h3>
            <p>{t('policy.categories')}</p>
          </div>
        </div>
        <div className="policy__stat-card">
          <div className="policy__stat-icon policy__stat-icon--amber">
            <FaFolderOpen />
          </div>
          <div className="policy__stat-info">
            <h3>{ungroupedCount}</h3>
            <p>{t('policy.uncategorized')}</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="policy__toolbar">
        <div className="policy__search">
          <FaSearch />
          <input
            type="text"
            placeholder={t('policy.searchTerms')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="policy__filters">
          <button
            type="button"
            className={`policy__expand-toggle${allCollapsed ? ' policy__expand-toggle--collapsed' : ''}`}
            onClick={toggleAllGroups}
            disabled={groupNames.length === 0}
            title={allCollapsed ? t('policy.expandAll') : t('policy.collapseAll')}
            aria-label={allCollapsed ? t('policy.expandAll') : t('policy.collapseAll')}
            aria-pressed={allCollapsed}
          >
            <span className="policy__expand-toggle-icon">
              {allCollapsed ? <FaAngleDoubleDown /> : <FaAngleDoubleUp />}
            </span>
            <span className="policy__expand-toggle-label">
              {allCollapsed ? t('policy.expandAll') : t('policy.collapseAll')}
            </span>
          </button>
          <CustomSelect
            value={filterGroup}
            onChange={setFilterGroup}
            className="policy__group-filter"
            options={[
              { value: 'all', label: t('policy.allGroups') },
              ...getGroups().map((group) => ({ value: group, label: group }))
            ]}
          />
        </div>
      </div>

      {/* List */}
      <div className="policy__content">
        {policies.length === 0 ? (
          <div className="policy__empty">
            <div className="policy__empty-icon"><FaScroll /></div>
            <h3>{t('policy.noTerms')}</h3>
            <p>{t('policy.noTermsHint')}</p>
            <button className="policy__empty-btn" onClick={toggleForm}>
              <FaPlus />
              {t('policy.addTerm')}
            </button>
          </div>
        ) : getFilteredPolicies().length === 0 ? (
          <div className="policy__empty">
            <div className="policy__empty-icon"><FaSearch /></div>
            <h3>{t('policy.noMatch')}</h3>
            <p>{t('policy.noMatchHint')}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="policy__groups">
              {Object.entries(groupedPolicies).map(([groupName, groupPolicies]) => {
                const collapsed = isGroupCollapsed(groupName);
                return (
                <div key={groupName} className={`policy__group${collapsed ? ' policy__group--collapsed' : ''}`}>
                  <div
                    className="policy__group-header"
                    role="button"
                    tabIndex={0}
                    aria-expanded={!collapsed}
                    aria-controls={`policy-items-${groupName}`}
                    onClick={() => toggleGroup(groupName)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleGroup(groupName);
                      }
                    }}
                    title={collapsed ? t('policy.expandGroup') : t('policy.collapseGroup')}
                  >
                    <div className="policy__group-title">
                      <span className="policy__group-icon"><FaFolderOpen /></span>
                      <h3 className="policy__group-name">{groupName}</h3>
                      <span className="policy__group-count">
                        {groupPolicies.length} {groupPolicies.length !== 1 ? 'terms' : 'term'}
                      </span>
                    </div>
                    <span className={`policy__group-chevron${collapsed ? ' policy__group-chevron--collapsed' : ''}`} aria-hidden="true">
                      <FaChevronDown />
                    </span>
                  </div>
                  <div
                    id={`policy-items-${groupName}`}
                    className={`policy__items-collapse${collapsed ? ' policy__items-collapse--collapsed' : ''}`}
                    aria-hidden={collapsed}
                  >
                  <div className="policy__items">
                    <SortableContext
                      id={`policy-group-${groupName}`}
                      items={groupPolicies.map(idOf)}
                      strategy={verticalListSortingStrategy}
                    >
                      {groupPolicies.map((p, idx) => (
                        <SortablePolicyItem
                          key={idOf(p)}
                          id={idOf(p)}
                          rawId={p._id || p.id}
                          index={idx}
                          content={p.content}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      ))}
                    </SortableContext>
                  </div>
                  </div>
                </div>
                );
              })}
            </div>
          </DndContext>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Modal
        isOpen={showForm}
        title={editingIndex > -1 ? t('policy.editTerm') : t('policy.addTermBtn')}
        onClose={handleCancel}
        size="medium"
      >
        <div className="policy__form">
          <div className="policy__form-group">
            <label>
              {t('policy.groupLabel')}
            </label>
            <CustomSelect
              value={selectedGroup}
              onChange={(val) => onGroupSelectChange({ target: { value: val } })}
              placeholder={t('policy.selectGroup')}
              options={[
                ...getGroups().map((g) => ({ value: g, label: g })),
                { value: '__new', label: t('policy.createNewGroup') }
              ]}
            />
          </div>

          {showNewGroupInput && (
            <div className="policy__form-group">
              <label>
                {t('policy.groupLabel')}
              </label>
              <div className="policy__new-group">
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g., Wedding Policy, Engagement Policy, Corporate Events..."
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="policy__form-group">
            <label>
              {editingIndex > -1 ? t('policy.editTerm') : t('policy.enterTerm')}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('policy.writeTerm')}
              rows={6}
            />
          </div>

          <div className="policy__form-actions">
            <button className="btn btn-secondary" onClick={handleCancel}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleAdd}>
              <FaPlus />
              {editingIndex > -1 ? t('policy.update') : t('policy.addTermBtn')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Policy;
